// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Eye } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CodeHighlight } from "@/components/shared/code-highlight";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

interface SettingValueCellProps {
  value: string | null | undefined;
  fieldType: string;
  name?: string;
  /** When true, renders JSON inline with full shiki highlight instead of truncated with eye icon */
  expanded?: boolean;
}

export function SettingValueCell({ value, fieldType, name, expanded }: SettingValueCellProps) {
  const { t } = useTranslation("back-office");
  const [jsonPreview, setJsonPreview] = useState(false);
  const val = value ?? "";
  const upper = val.toUpperCase();

  if (!value || upper === "NULL") {
    return (
      <span className="inline-flex items-center rounded px-2 py-0.5 text-xs text-muted-foreground border border-dashed border-muted-foreground/40 bg-muted/50">
        NULL
      </span>
    );
  }
  if (upper === "EMPTY") {
    return (
      <span className="inline-flex items-center rounded px-2 py-0.5 text-xs text-muted-foreground border border-dashed border-muted-foreground/40 bg-muted/50">
        EMPTY
      </span>
    );
  }
  if (fieldType === "boolean") {
    const checked = upper === "TRUE" || upper === "1";
    return <Switch checked={checked} className="pointer-events-none" />;
  }
  if (fieldType === "number") {
    return <span className="tabular-nums">{val}</span>;
  }
  if (fieldType === "json") {
    if (expanded) {
      return <CodeHighlight value={val} lang="json" />;
    }
    return (
      <>
        <div className="flex items-center gap-1.5 max-w-[300px]">
          <span className="truncate font-mono text-xs">{val}</span>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 h-5 w-5 text-muted-foreground hover:text-foreground"
            onClick={() => setJsonPreview(true)}
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
        </div>
        <Dialog open={jsonPreview} onOpenChange={setJsonPreview}>
          <DialogContent
            className="max-h-[80vh] flex flex-col p-0"
            style={{ width: "fit-content", minWidth: "42rem", maxWidth: "80vw" }}
          >
            <DialogHeader className="p-6 pb-0">
              <DialogTitle>{name ?? "JSON"}</DialogTitle>
              <DialogDescription>{t("settings.list.jsonPreview.description")}</DialogDescription>
            </DialogHeader>
            <div className="min-h-0 overflow-auto p-6 pt-2">
              <CodeHighlight value={val} lang="json" />
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  // Deliberately no `title`: the native tooltip is OS-drawn, so it ignores the
  // theme, lags ~1s and is unreachable by keyboard.
  return <span className="max-w-[200px] truncate block">{val}</span>;
}
